import { ChevronDown, ChevronRight, Loader2, MapPin, X } from 'lucide-react';
import type React from 'react';
import type { RefObject } from 'react';
import { type Control, Controller, type FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/new-ui/button';
import { Input } from '@/components/new-ui/input';
import { Label } from '@/components/new-ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/new-ui/select';
import { Separator } from '@/components/new-ui/separator';
import { MapBox } from '@/components/ui/map';
import type { SelectOption } from '@/components/ui/select';
import { PROPERTY_LEVELS } from '@/constants/property-level';

import type { EstateSchemaType } from '../schemas/estate';
import type { OnSelectSuggestion } from './address-autocomplete';
import { AddressAutocomplete } from './address-autocomplete';

type FieldProps = {
  label: string;
  error?: string;
  htmlFor?: string;
  children: React.ReactNode;
};

const getErrorId = (id: string) => `${id}-error`;

// Marks a control invalid and points it at the error text of its Field
const getInvalidProps = (id: string, error?: string) =>
  error ? { 'aria-invalid': true, 'aria-describedby': getErrorId(id) } : {};

const Field = ({ label, error, htmlFor, children }: FieldProps) => (
  <div className="space-y-1.5">
    <Label htmlFor={htmlFor}>{label}</Label>
    {children}
    {error ? (
      <p
        id={htmlFor ? getErrorId(htmlFor) : undefined}
        role="alert"
        className="text-xs text-destructive"
      >
        {error}
      </p>
    ) : null}
  </div>
);

type SelectFieldProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  clearLabel: string;
  /** Validation message of the field, marks the trigger invalid */
  error?: string;
  /** The Controller's `field.ref`, so a failed submit can focus this select */
  triggerRef?: React.Ref<HTMLButtonElement>;
};

// Radix Select rejects an empty item value, so an option with an empty id (e.g. "not specified") uses this one
const EMPTY_OPTION_VALUE = '__empty__';

const SelectField = ({
  id,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  clearable,
  clearLabel,
  error,
  triggerRef,
}: SelectFieldProps) => (
  <div className="flex gap-2">
    <Select
      value={
        value === '' && options.some((option) => option.id === '') ? EMPTY_OPTION_VALUE : value
      }
      onValueChange={(next) => {
        // Inside a form Radix reports '' when the value is set before its option has loaded.
        // A real empty choice comes as the sentinel, and the clear button calls onChange itself
        if (next === '') return;
        onChange(next === EMPTY_OPTION_VALUE ? '' : next);
      }}
      disabled={disabled}
    >
      <SelectTrigger ref={triggerRef} id={id} className="flex-1" {...getInvalidProps(id, error)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.id} value={option.id || EMPTY_OPTION_VALUE}>
            {option.title}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    {clearable && value && !disabled ? (
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={() => onChange('')}
        title={clearLabel}
        aria-label={clearLabel}
      >
        <X className="h-4 w-4" />
      </Button>
    ) : null}
  </div>
);

export type EstateFormNewUiProps = {
  control: Control<EstateSchemaType>;
  errors: FieldErrors<EstateSchemaType>;
  loading: boolean;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  /** Shows a Cancel button in the footer, used by the edit dialog */
  onCancel?: () => void;
  /** Submit button text, defaults to create estate / project */
  submitLabel?: string;
  /** Submit button text while saving, defaults to creating */
  loadingLabel?: string;
  propertyLevel: string;
  addressData: EstateSchemaType['addressData'];
  propertyTypeOptions: SelectOption[];
  marketTypeOptions: SelectOption[];
  projectOptions: SelectOption[];
  tenantOptions: SelectOption[];
  availabilityOptions: SelectOption[];
  countries: SelectOption[];
  ipLocation?: { latitude: number; longitude: number } | null;
  propertyLevelDisabled?: boolean;
  projectDisabled?: boolean;
  tenantDisabled?: boolean;
  isAddressOptional: boolean;
  isAddressSectionExpanded: boolean;
  hasAddressValidationError: boolean;
  addressSectionRef: RefObject<HTMLDivElement>;
  onToggleAddress: () => void;
  onSelectSuggestion: OnSelectSuggestion;
  onPropertyLevelChange: (value: string) => void;
  /** Address the last suggestion filled in, so the autocomplete does not search it again */
  selectedAddressValue?: string;
  isDetectingLocation?: boolean;
  onUseCurrentLocation?: () => void;
  /** Extra fields shown above the address, for example the catalog presets of a new project */
  children?: React.ReactNode;
};

/** Create / edit estate form in the layout of the redesign, same fields and behaviour as the default one. */
export const EstateFormNewUi: React.FC<EstateFormNewUiProps> = ({
  control,
  errors,
  loading,
  onSubmit,
  onCancel,
  submitLabel,
  loadingLabel,
  propertyLevel,
  addressData,
  propertyTypeOptions,
  marketTypeOptions,
  projectOptions,
  tenantOptions,
  availabilityOptions,
  countries,
  ipLocation,
  propertyLevelDisabled,
  projectDisabled,
  tenantDisabled,
  isAddressOptional,
  isAddressSectionExpanded,
  hasAddressValidationError,
  addressSectionRef,
  onToggleAddress,
  onSelectSuggestion,
  onPropertyLevelChange,
  selectedAddressValue,
  isDetectingLocation = false,
  onUseCurrentLocation,
  children,
}) => {
  const { t } = useTranslation();
  const isProject = propertyLevel === PROPERTY_LEVELS.PROJECT;
  const clearLabel = t('clear');
  // Zoom, map key and position all need both coordinates
  // 0 is a valid latitude or longitude, so only a missing value counts as no coordinates
  const hasCoordinates = addressData.lat != null && addressData.long != null;

  return (
    <form className="relative flex min-h-0 flex-1 flex-col" onSubmit={onSubmit}>
      {loading ? (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/60">
          <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
        </div>
      ) : null}
      {/* bottom padding keeps the last field off the sticky footer when scrolled to the end */}
      <div className="flex-1 space-y-4 overflow-y-auto px-6 pb-6 pt-2">
        <Controller
          name="estateData.name"
          control={control}
          render={({ field }) => (
            <Field
              label={`${t('name')} *`}
              htmlFor="estate-form-name"
              error={errors.estateData?.name?.message}
            >
              <Input
                {...field}
                id="estate-form-name"
                {...getInvalidProps('estate-form-name', errors.estateData?.name?.message)}
                autoFocus
              />
            </Field>
          )}
        />

        <Controller
          name="estateData.tenantId"
          control={control}
          render={({ field }) => (
            <Field
              label={`${t('tenant')} *`}
              htmlFor="estate-form-tenant"
              error={errors.estateData?.tenantId?.message}
            >
              <SelectField
                id="estate-form-tenant"
                value={field.value ?? ''}
                triggerRef={field.ref}
                onChange={field.onChange}
                options={tenantOptions}
                placeholder={t('select')}
                // Kept with no tenants too, so the required tenant error stays visible
                disabled={tenantDisabled || !tenantOptions.length}
                clearLabel={clearLabel}
                error={errors.estateData?.tenantId?.message}
              />
            </Field>
          )}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Controller
            name="estateData.propertyType"
            control={control}
            render={({ field }) => (
              <Field
                label={t('propertyType')}
                htmlFor="estate-form-property-type"
                error={errors.estateData?.propertyType?.message}
              >
                <SelectField
                  id="estate-form-property-type"
                  value={field.value ?? ''}
                  triggerRef={field.ref}
                  onChange={field.onChange}
                  options={propertyTypeOptions}
                  clearable
                  clearLabel={clearLabel}
                  error={errors.estateData?.propertyType?.message}
                />
              </Field>
            )}
          />
          <Controller
            name="estateData.marketType"
            control={control}
            render={({ field }) => (
              <Field
                label={t('marketingType')}
                htmlFor="estate-form-market-type"
                error={errors.estateData?.marketType?.message}
              >
                <SelectField
                  id="estate-form-market-type"
                  value={field.value ?? ''}
                  triggerRef={field.ref}
                  onChange={field.onChange}
                  options={marketTypeOptions}
                  clearable
                  clearLabel={clearLabel}
                  error={errors.estateData?.marketType?.message}
                />
              </Field>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Controller
            name="estateData.propertyLevel"
            control={control}
            render={({ field }) => (
              <Field
                label={`${t('propertyLevel')} *`}
                htmlFor="estate-form-property-level"
                error={errors.estateData?.propertyLevel?.message}
              >
                <SelectField
                  id="estate-form-property-level"
                  value={field.value ?? ''}
                  triggerRef={field.ref}
                  onChange={(value) => {
                    field.onChange(value);
                    onPropertyLevelChange(value);
                  }}
                  options={[
                    { title: t('project'), id: PROPERTY_LEVELS.PROJECT },
                    { title: t('Estate'), id: PROPERTY_LEVELS.OBJECT },
                  ]}
                  disabled={propertyLevelDisabled}
                  clearLabel={clearLabel}
                  error={errors.estateData?.propertyLevel?.message}
                />
              </Field>
            )}
          />
          {!isProject ? (
            <Controller
              name="estateData.projectId"
              control={control}
              render={({ field }) => (
                <Field
                  label={t('project')}
                  htmlFor="estate-form-project"
                  error={errors.estateData?.projectId?.message}
                >
                  <SelectField
                    id="estate-form-project"
                    value={field.value ?? ''}
                    triggerRef={field.ref}
                    onChange={field.onChange}
                    options={projectOptions}
                    placeholder={t('selectProject')}
                    disabled={projectDisabled}
                    clearable
                    clearLabel={clearLabel}
                    error={errors.estateData?.projectId?.message}
                  />
                </Field>
              )}
            />
          ) : null}
        </div>

        {propertyLevel === PROPERTY_LEVELS.OBJECT ? (
          <Controller
            name="estateData.availability"
            control={control}
            render={({ field }) => (
              <Field label={t('availability')} htmlFor="estate-form-availability">
                <SelectField
                  id="estate-form-availability"
                  value={field.value ?? ''}
                  triggerRef={field.ref}
                  onChange={(value) =>
                    field.onChange(
                      (value || null) as EstateSchemaType['estateData']['availability']
                    )
                  }
                  options={availabilityOptions}
                  clearLabel={clearLabel}
                />
              </Field>
            )}
          />
        ) : null}

        <Separator />

        {children}
        <div ref={addressSectionRef} className="space-y-4">
          {isAddressOptional ? (
            <button
              type="button"
              className="flex w-full items-center gap-2 text-left text-sm font-medium"
              aria-expanded={isAddressSectionExpanded}
              aria-label={
                isAddressSectionExpanded ? t('collapseAddressSection') : t('expandAddressSection')
              }
              onClick={onToggleAddress}
            >
              {isAddressSectionExpanded ? (
                <ChevronDown className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronRight className="h-4 w-4" aria-hidden />
              )}
              <span>{t('address')}</span>
            </button>
          ) : null}

          {isAddressOptional && !isAddressSectionExpanded ? (
            <div className="break-words text-sm">
              <p className="text-muted-foreground">
                {addressData.addressAutoComplete || addressData.address}
              </p>
              {hasAddressValidationError ? (
                <p className="mt-2 text-destructive">{t('openAddressAndCompleteFields')}</p>
              ) : null}
            </div>
          ) : null}

          {isAddressSectionExpanded ? (
            <>
              <Controller
                name="addressData.addressAutoComplete"
                control={control}
                render={({ field }) => (
                  <Field
                    label={isAddressOptional ? t('address') : `${t('address')} *`}
                    htmlFor="estate-form-address"
                    error={errors.addressData?.addressAutoComplete?.message}
                  >
                    <div className="flex gap-2">
                      <AddressAutocomplete
                        {...field}
                        id="estate-form-address"
                        {...getInvalidProps(
                          'estate-form-address',
                          errors.addressData?.addressAutoComplete?.message
                        )}
                        newUi
                        selectedValue={selectedAddressValue}
                        onSelectSuggestion={onSelectSuggestion}
                      />
                      {onUseCurrentLocation ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="shrink-0"
                          disabled={isDetectingLocation}
                          onClick={onUseCurrentLocation}
                          title={t('useCurrentLocation')}
                          aria-label={t('useCurrentLocation')}
                        >
                          {isDetectingLocation ? (
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                          ) : (
                            <MapPin className="h-4 w-4" aria-hidden />
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </Field>
                )}
              />
              <div className="h-48 w-full overflow-hidden rounded-md border bg-muted [&_.mapboxgl-canvas]:!h-full [&_.mapboxgl-canvas]:!w-full [&_.mapboxgl-map]:!h-full [&_.mapboxgl-map]:!w-full">
                <MapBox
                  key={
                    hasCoordinates ? `${addressData.lat}:${addressData.long}` : ipLocation?.latitude
                  }
                  fullscreenControl={false}
                  zoom={hasCoordinates ? 12 : 4}
                  {...(hasCoordinates
                    ? {
                        latitude: Number(addressData.lat),
                        longitude: Number(addressData.long),
                      }
                    : ipLocation
                      ? {
                          latitude: ipLocation.latitude,
                          longitude: ipLocation.longitude,
                        }
                      : undefined)}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Controller
                  name="addressData.street"
                  control={control}
                  render={({ field }) => (
                    <Field
                      label={t('street')}
                      htmlFor="estate-form-street"
                      error={errors.addressData?.street?.message}
                    >
                      <Input
                        {...field}
                        id="estate-form-street"
                        {...getInvalidProps(
                          'estate-form-street',
                          errors.addressData?.street?.message
                        )}
                      />
                    </Field>
                  )}
                />
                <Controller
                  name="addressData.houseNumber"
                  control={control}
                  render={({ field }) => (
                    <Field
                      label={t('houseNumber')}
                      htmlFor="estate-form-house-number"
                      error={errors.addressData?.houseNumber?.message}
                    >
                      <Input
                        {...field}
                        id="estate-form-house-number"
                        {...getInvalidProps(
                          'estate-form-house-number',
                          errors.addressData?.houseNumber?.message
                        )}
                      />
                    </Field>
                  )}
                />
                <Controller
                  name="addressData.city"
                  control={control}
                  render={({ field }) => (
                    <Field
                      label={t('city')}
                      htmlFor="estate-form-city"
                      error={errors.addressData?.city?.message}
                    >
                      <Input
                        {...field}
                        id="estate-form-city"
                        {...getInvalidProps('estate-form-city', errors.addressData?.city?.message)}
                      />
                    </Field>
                  )}
                />
                <Controller
                  name="addressData.postalCode"
                  control={control}
                  render={({ field }) => (
                    <Field
                      label={t('postalCode')}
                      htmlFor="estate-form-postal-code"
                      error={errors.addressData?.postalCode?.message}
                    >
                      <Input
                        {...field}
                        id="estate-form-postal-code"
                        {...getInvalidProps(
                          'estate-form-postal-code',
                          errors.addressData?.postalCode?.message
                        )}
                      />
                    </Field>
                  )}
                />
                <Controller
                  name="addressData.countryId"
                  control={control}
                  render={({ field }) => (
                    <Field
                      label={t('country')}
                      htmlFor="estate-form-country"
                      error={errors.addressData?.countryId?.message}
                    >
                      <SelectField
                        id="estate-form-country"
                        value={field.value ?? ''}
                        triggerRef={field.ref}
                        onChange={field.onChange}
                        options={countries}
                        clearable
                        clearLabel={clearLabel}
                        error={errors.addressData?.countryId?.message}
                      />
                    </Field>
                  )}
                />
              </div>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-3 border-t border-border px-6 pb-6 pt-4">
        {onCancel ? (
          <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
            {t('cancel')}
          </Button>
        ) : null}
        <Button type="submit" className="flex-1" disabled={loading}>
          {loading
            ? (loadingLabel ?? t('creating'))
            : (submitLabel ?? (isProject ? t('create-project') : t('create-estate')))}
        </Button>
      </div>
    </form>
  );
};
